import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FaTachometerAlt, 
  FaUsers, 
  FaBuilding, 
  FaChartBar,
  FaCog,
  FaSignOutAlt,
  FaBars,
  FaMoon,
  FaSun,
  FaChevronDown,
  FaChevronRight,
  FaUser,
  FaHistory,
  FaTimes,
  FaSearch,
  FaBell,
  FaFileInvoiceDollar,
  FaUserShield,
  FaWhatsapp,
  FaCreditCard
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { apiGet } from '../../utils/api';
import LanguageSelector from '../../components/ui/LanguageSelector.jsx';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const { handleLogout, user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();

  // Charger le nombre de notifications toutes les 30 secondes
  useEffect(() => {
    loadNotificationCount();
    const interval = setInterval(loadNotificationCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadNotificationCount = async () => {
    try {
      const response = await apiGet('/admin/notifications/count');
      setNotificationCount(response.count || 0);
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    }
  };

  const toggleDropdown = (menuId) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const menuItems = [
    { id: 'dashboard', label: t('admin.menu.dashboard'), icon: FaTachometerAlt, path: '/adminpanel' },
    { id: 'users', label: t('admin.menu.users'), icon: FaUsers, path: '/adminpanel/users' },
    { id: 'companies', label: t('admin.menu.companies'), icon: FaBuilding, path: '/adminpanel/companies' },
    { id: 'adminPanels', label: t('admin.menu.adminPanels'), icon: FaUserShield, path: '/adminpanel/admin-panels' },
    { id: 'invoices', label: t('admin.menu.invoices'), icon: FaFileInvoiceDollar, path: '/adminpanel/factures' },
    { id: 'history', label: t('admin.menu.history'), icon: FaHistory, path: '/adminpanel/history' },
    { id: 'whatsapp', label: 'WhatsApp', icon: FaWhatsapp, path: '/adminpanel/whatsapp' },
    { id: 'ligdicash', label: 'Paiements', icon: FaCreditCard, path: '/adminpanel/paiements' },
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'}`}>
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 shadow-md transform transition-all duration-300 ease-in-out flex flex-col backdrop-blur-sm ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 ${isDark ? 'bg-gray-800/95 border-r border-gray-700' : 'bg-white/95 border-r border-gray-200'}`}>
        
        {/* Brand */}
        <div className={`flex items-center h-16 px-6 border-b ${isDark ? 'border-gray-700 bg-gradient-to-r from-blue-600/20 to-purple-600/20' : 'border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50'}`}>
          <div className="flex items-center">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl mr-3 shadow-lg">
              <FaChartBar className="text-white text-lg" />
            </div>
            <span className={`text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent`}>SecureTech</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className={`lg:hidden ml-auto ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaTimes />
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 px-4 py-6 overflow-y-auto">
          <div className="mb-6">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 px-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('common.menu', 'MENU')}</h3>
            <div className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const isOpen = openDropdowns[item.id];
                
                return (
                  <div key={item.id}>
                    {item.hasDropdown ? (
                      <>
                        <button
                          onClick={() => toggleDropdown(item.id)}
                          className={`w-full flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                            isActive 
                              ? 'bg-blue-50 text-blue-700' 
                              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                          }`}
                        >
                          <Icon className="mr-3 text-base" />
                          <span className="flex-1 text-left">{item.label}</span>
                          <FaChevronRight className={`text-xs text-gray-400 transition-transform duration-200 ${
                            isOpen ? 'rotate-90' : ''
                          }`} />
                        </button>
                        {isOpen && item.subItems && (
                          <div className="ml-8 mt-1 space-y-1">
                            {item.subItems.map((subItem, idx) => (
                              <Link
                                key={idx}
                                to={subItem.path}
                                className="block px-3 py-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
                              >
                                {subItem.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.path}
                        className={`flex items-center px-3 py-3 rounded-xl text-sm font-medium transition-all duration-300 group ${
                          isActive 
                            ? isDark 
                              ? 'bg-gradient-to-r from-blue-600/30 to-purple-600/30 text-blue-400 shadow-lg border border-blue-500/30' 
                              : 'bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 shadow-md border border-blue-200'
                            : isDark
                              ? 'text-gray-300 hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-600/50 hover:text-white hover:shadow-md'
                              : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-gray-900 hover:shadow-sm'
                        }`}
                      >
                        <Icon className="mr-3 text-base group-hover:scale-110 transition-transform duration-200" />
                        <span className="flex-1">{item.label}</span>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        </nav>

        {/* Footer - Langue et Déconnexion */}
        <div className={`px-4 py-4 border-t space-y-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {/* Sélecteur de Langue */}
          <LanguageSelector dropUp={true} />
          
          {/* Bouton Déconnexion */}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 group ${
              isDark 
                ? 'text-white hover:bg-red-900/30' 
                : 'text-red-600 hover:bg-red-50 hover:text-red-700'
            }`}
          >
            <FaSignOutAlt className="mr-3 text-base group-hover:scale-110 transition-transform" />
            <span className="flex-1 text-left">{t('admin.menu.logout')}</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className={`shadow-md border-b sticky top-0 z-30 backdrop-blur-sm ${isDark ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 border-gray-200'}`}>
          <div className="flex items-center justify-between h-16 px-6">
            {/* Left side - Menu toggle */}
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className={`lg:hidden p-2 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              >
                <FaBars className="text-lg" />
              </button>
              <button className={`hidden lg:block p-2 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}>
                <FaBars className="text-lg" />
              </button>
            </div>
            
            {/* Center - Search bar */}
            <div className="flex-1 max-w-2xl mx-8 hidden md:block">
              <div className="relative">
                <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder={t('admin.header.searchPlaceholder')}
                  className={`w-full pl-10 pr-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-gray-50 border-gray-200 text-gray-900'
                  }`}
                />
              </div>
            </div>
            
            {/* Right side - Actions */}
            <div className="flex items-center space-x-3">
              {/* Notifications */}
              <div className="relative">
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className={`relative p-2 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  <FaBell className="text-lg" />
                  {notificationCount > 0 && (
                    <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </button>
                
                {/* Dropdown notifications */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-md border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Notifications Admin</h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {notificationCount === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                          Aucune nouvelle notification
                        </div>
                      ) : (
                        <div className="p-4 text-center text-gray-500">
                          {notificationCount} notification(s)
                        </div>
                      )}
                    </div>
                    <div className="p-4 border-t border-gray-200">
                      <button 
                        onClick={() => setShowNotifications(false)}
                        className="w-full text-sm text-blue-600 hover:text-blue-800"
                      >
                        Marquer tout comme lu
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Dark mode toggle */}
              <button 
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              >
                {isDark ? <FaSun className="text-lg" /> : <FaMoon className="text-lg" />}
              </button>
              
              {/* User dropdown */}
              <div className={`relative flex items-center space-x-2 pl-3 border-l ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="hidden sm:block text-right">
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.nom_complet || 'Admin'}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('admin.header.adminRole')}</p>
                </div>
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center cursor-pointer hover:shadow-lg transition-shadow">
                  <span className="text-white text-sm font-semibold">
                    {user?.nom_complet?.charAt(0) || 'A'}
                  </span>
                </div>
                <button className={`transition-colors ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
                  <FaChevronDown className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 min-h-screen">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-30 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
